import React, { useMemo } from "react";
import { cn } from "@/lib/utils";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfLocalDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function calendarDayKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function dueCalendarDayKey(post) {
  const raw = post?.due_date ?? post?.dueDate;
  if (!raw) return null;
  const d = raw instanceof Date ? raw : new Date(raw);
  if (!Number.isFinite(d.getTime())) return null;
  return calendarDayKey(d);
}

/** Monday 00:00 local for the week containing `now`. */
function mondayOfWeek(now) {
  const day = startOfLocalDay(now);
  const dow = day.getDay(); // 0=Sun … 6=Sat
  const offsetFromMonday = dow === 0 ? -6 : 1 - dow;
  day.setDate(day.getDate() + offsetFromMonday);
  return day;
}

/**
 * Read-only Mon–Sun strip for current week dues (today + this_week items).
 * Overdue is a callout, not plotted on past days.
 */
export default function MyWorkWeekStrip({
  buckets,
  overdueCount = 0,
  onOverdueClick,
  onDayClick,
  className,
}) {
  const { days } = useMemo(() => {
    const now = new Date();
    const monday = mondayOfWeek(now);
    const today = calendarDayKey(now);
    const weekItems = [
      ...(buckets?.today || []),
      ...(buckets?.this_week || []),
    ];
    const countByDay = new Map();
    for (const item of weekItems) {
      const key = dueCalendarDayKey(item?.post);
      if (!key) continue;
      countByDay.set(key, (countByDay.get(key) || 0) + 1);
    }
    const cols = DAY_LABELS.map((label, i) => {
      const d = new Date(
        monday.getFullYear(),
        monday.getMonth(),
        monday.getDate() + i
      );
      const key = calendarDayKey(d);
      return {
        label,
        key,
        dateNum: d.getDate(),
        count: countByDay.get(key) || 0,
        isToday: key === today,
      };
    });
    return { days: cols };
  }, [buckets]);

  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-background px-3 py-3",
        className
      )}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs font-medium text-foreground">This week</p>
        <p className="text-[11px] text-muted-foreground">
          Due dates Mon–Sun · read-only
        </p>
      </div>
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={onOverdueClick}
          disabled={!overdueCount}
          title="Jump to overdue work"
          className={cn(
            "shrink-0 w-16 rounded-md border px-1.5 py-2 flex flex-col items-center justify-center gap-0.5 transition-colors",
            overdueCount > 0
              ? "border-destructive/40 text-destructive hover:bg-destructive/5"
              : "border-border text-muted-foreground opacity-60 cursor-default"
          )}
        >
          <span className="text-[10px] font-medium uppercase tracking-wide">
            Overdue
          </span>
          <span className="text-sm font-semibold tabular-nums">
            {overdueCount}
          </span>
        </button>
        <div className="min-w-0 flex-1 grid grid-cols-7 gap-1">
          {days.map((day) => {
            const hasWork = day.count > 0;
            const dots = Math.min(day.count, 3);
            return (
              <button
                key={day.key}
                type="button"
                onClick={() => onDayClick?.(day)}
                title={
                  day.isToday
                    ? `${day.count} due today`
                    : `${day.count} due ${day.label} ${day.dateNum}`
                }
                className={cn(
                  "rounded-md border px-0.5 py-1.5 flex flex-col items-center gap-1 transition-colors min-h-[3.25rem]",
                  day.isToday
                    ? "border-foreground/30 bg-muted/40"
                    : "border-border bg-background",
                  hasWork
                    ? "hover:bg-muted/60"
                    : "hover:bg-muted/30 text-muted-foreground"
                )}
              >
                <span className="text-[10px] font-medium text-muted-foreground">
                  {day.label}
                </span>
                <span
                  className={cn(
                    "text-xs tabular-nums",
                    day.isToday ? "font-semibold text-foreground" : ""
                  )}
                >
                  {day.dateNum}
                </span>
                <span className="flex items-center justify-center gap-0.5 h-2">
                  {hasWork ? (
                    Array.from({ length: dots }).map((_, i) => (
                      <span
                        key={i}
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          day.isToday ? "bg-foreground" : "bg-muted-foreground"
                        )}
                      />
                    ))
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-transparent" />
                  )}
                </span>
                {day.count > 0 ? (
                  <span className="text-[10px] tabular-nums text-muted-foreground leading-none">
                    {day.count}
                  </span>
                ) : (
                  <span className="text-[10px] leading-none opacity-0">0</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
